import React, { useState } from 'react';
import styled from 'styled-components';
import { Check, User, Users, UserCog, X } from 'lucide-react';
import StaffMultiPicker from './StaffMultiPicker';
import type { Assignee } from '../../types/assignee';
import { formatAllAssigneeNames, formatAssigneeNames } from '../../utils/assignees';

interface AssigneesControlProps {
  assignees: Assignee[]; // ריק ⇒ לא-משויך (למשל תזכורת ממאגר המנהלים)
  /** מבצע את קריאת ה-API להחלפת סט המשויכים. יש לזרוק שגיאה בכשל כדי שהעורך יישאר פתוח לניסיון חוזר. */
  onSave: (userIds: number[]) => Promise<void>;
  disabled?: boolean;
}

const Wrap = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const AssigneeText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  color: var(--text-secondary);
`;

const ChangeButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  color: var(--primary-color);
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  padding: 2px 6px;
  border-radius: 5px;
  transition: background 0.2s ease;

  &:hover {
    background: var(--primary-light);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EditRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 6px;
  min-width: 220px;
  max-width: 360px;
`;

const PickerSlot = styled.div`
  flex: 1;
  min-width: 0;
`;

const Actions = styled.div`
  display: flex;
  gap: 2px;
  padding-top: 2px;
`;

const IconButton = styled.button<{ $variant: 'save' | 'cancel' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: none;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  color: ${props => props.$variant === 'save' ? 'var(--primary-color)' : 'var(--text-secondary)'};
  transition: background 0.2s ease, color 0.2s ease;

  &:hover:not(:disabled) {
    background: ${props => props.$variant === 'save' ? 'var(--primary-light)' : 'transparent'};
    color: ${props => props.$variant === 'save' ? 'var(--primary-color)' : 'var(--error-color)'};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const sameIds = (a: number[], b: number[]) =>
  a.length === b.length && a.every((id) => b.includes(id));

/**
 * שליטה קומפקטית בשיוך של פריט מתוזמן לכמה עובדים: מציגה את שמות המשויכים + כפתור "שנה משויכים".
 * בלחיצה נפתח עורך inline (StaffMultiPicker) עם שמירה/ביטול מפורשים. שמירה מושבתת כשהרשימה ריקה או לא השתנתה.
 */
const AssigneesControl: React.FC<AssigneesControlProps> = ({ assignees, onSave, disabled }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<number[]>([]);

  const currentIds = assignees.map((a) => a.userId);
  const hasAssignees = assignees.length > 0;

  const startEditing = () => {
    setDraft(currentIds);
    setEditing(true);
  };

  const handleSave = async () => {
    if (draft.length === 0 || sameIds(draft, currentIds)) return;
    setSaving(true);
    try {
      await onSave(draft);
      setEditing(false);
    } catch {
      // הודעת השגיאה מוצגת ע"י הקורא (toast) — משאירים את העורך פתוח לניסיון חוזר
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <EditRow>
        <PickerSlot>
          <StaffMultiPicker
            label=""
            value={draft}
            onChange={setDraft}
            knownAssignees={assignees}
            disabled={saving}
          />
        </PickerSlot>
        <Actions>
          <IconButton
            type="button"
            $variant="save"
            onClick={handleSave}
            disabled={saving || draft.length === 0 || sameIds(draft, currentIds)}
            aria-label="שמור משויכים"
            title={draft.length === 0 ? 'יש לבחור לפחות עובד אחד' : 'שמור'}
          >
            <Check size={16} />
          </IconButton>
          <IconButton
            type="button"
            $variant="cancel"
            onClick={() => setEditing(false)}
            disabled={saving}
            aria-label="ביטול"
            title="ביטול"
          >
            <X size={16} />
          </IconButton>
        </Actions>
      </EditRow>
    );
  }

  return (
    <Wrap>
      <AssigneeText title={formatAllAssigneeNames(assignees)}>
        {assignees.length > 1 ? <Users size={14} strokeWidth={2} /> : <User size={14} strokeWidth={2} />}
        {formatAssigneeNames(assignees)}
      </AssigneeText>
      <ChangeButton type="button" onClick={startEditing} disabled={disabled}>
        <UserCog size={13} strokeWidth={2} />
        {hasAssignees ? 'שנה משויכים' : 'שייך'}
      </ChangeButton>
    </Wrap>
  );
};

export default AssigneesControl;
